import { useQueryClient } from '@tanstack/react-query';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import {
  useFetchFAQ,
  useUpdateFaQs,
} from '../../../../hooks/apiHooks/twinHooks';
import { useTwinData } from '../../../../hooks/storeHooks/useTwin';
import { Heading } from '../Heading';
import { CsvUpload } from './CsvUpload';
import { FaqItem } from './FaqItem';
import { Faq } from './FaqTab';

interface FaqManagementSectionProps {
  openNextTab: () => void;
  isReadyStatus: boolean;
  mentorId: string;
}

const FaqManagementSection: React.FC<FaqManagementSectionProps> = memo(
  ({ openNextTab, mentorId, isReadyStatus }) => {
    const { t } = useTranslation();
    const { faqs, addFaq, updateFaq, deleteFaq, setFaqs } = useTwinData();
    const hasInitializedRef = useRef(false);
    const queryClient = useQueryClient();
    const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(
      new Set(),
    );
    const [shouldHighlightUnsaved, setShouldHighlightUnsaved] = useState(false);

    const { mutateAsync: updateFaQs, isPending: isUpdatingFaQs } =
      useUpdateFaQs();

    const { data: faqsData, isFetching: faqsFetching } = useFetchFAQ({
      mentorId,
    });

    const handleAddQuestion = () => {
      addFaq({
        question: '',
        answer: '',
        id: crypto.randomUUID(),
        isNew: true,
      });
    };

    useEffect(() => {
      if (faqsData && !hasInitializedRef.current) {
        setFaqs(faqsData.items);
        hasInitializedRef.current = true;
      }
    }, [faqsData, setFaqs]);

    const handleChangeItem = (faq: Faq) => {
      updateFaq(faq);
    };

    const handleDeleteItem = (id: string) => {
      deleteFaq(id);
      // Remove from unsaved changes when deleted
      setUnsavedChanges((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    };

    const handleUnsavedChanges = (id: string, hasChanges: boolean) => {
      setUnsavedChanges((prev) => {
        const next = new Set(prev);
        if (hasChanges) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
      // Reset highlight when user saves changes
      if (!hasChanges && shouldHighlightUnsaved) {
        setShouldHighlightUnsaved(false);
      }
    };

    const isValid = useMemo(() => {
      return faqs.every((faq) => faq.question.trim() && faq.answer.trim());
    }, [faqs]);

    const hasNonEmptyFaqs = useMemo(() => {
      return faqs.some((faq) => faq.question.trim() || faq.answer.trim());
    }, [faqs]);

    const canSave = useMemo(() => {
      if (!hasNonEmptyFaqs) return true;
      return isValid;
    }, [hasNonEmptyFaqs, isValid]);

    const handleContinue = () => {
      // Check if there are any unsaved changes
      if (unsavedChanges.size > 0) {
        setShouldHighlightUnsaved(true);
        toast.error(t('twin.saveBeforeContinuing'));
        return;
      }

      // Allow if all FAQs are empty
      if (!hasNonEmptyFaqs) {
        openNextTab();
        return;
      }

      // Allow if all FAQs are valid
      if (!isValid) {
        toast.error(t('twin.completeFaqFields'));
        return;
      }

      try {
        openNextTab();
      } catch (error) {
        console.error('Error setting FAQs:', error);
      }
    };

    const handleSave = async () => {
      if (!mentorId || !canSave) return;
      try {
        const nonEmptyFaqs = faqs.filter(
          (faq) => faq.question.trim() || faq.answer.trim(),
        );

        const processedFaqs = nonEmptyFaqs.map((faq) => {
          if (faq.isNew) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, isNew, ...rest } = faq;
            return rest;
          }
          return faq;
        });

        await updateFaQs({
          mentorId,
          faqs: processedFaqs,
        });

        // Invalidate and refetch the FAQ query to get the latest data from server
        await queryClient.invalidateQueries({
          queryKey: ['fetchFAQ', mentorId],
        });

        // Reset the initialization flag so the useEffect can update from the fresh server data
        hasInitializedRef.current = false;

        toast.success(t('twin.faqsSaved'));
      } catch {
        toast.error(t('twin.faqsSaveFailed'));
      }
    };

    return (
      <div className="max-w-full w-[600px] w- mb-auto sm:mb-0">
        <Heading
          title={t('twin.faqsTitle')}
          subtitle={t('twin.faqsSubtitle')}
        />

        <div className="h-full sm:h-[50vh] flex flex-col items-center justify-center mb-8">
          {faqsFetching ? (
            <div className="mt-4 flex  justify-center">
              <AnimatedSpinner size={24} />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-4 mb-2">
                <CsvUpload />
                <button className="btn btn-primary" onClick={handleAddQuestion}>
                  <i className="fa-solid fa-plus mr-2" />
                  {t('twin.addQuestion')}
                </button>
              </div>

              <a
                href="/assets/faq_sample.csv"
                download="faq_sample.csv"
                className="underline mb-8">
                {t('twin.downloadSampleCsv')}
              </a>

              <div className="flex-1 overflow-auto h-full w-full">
                {faqs.map((faq) => (
                  <FaqItem
                    key={faq.id}
                    faq={faq}
                    onChange={handleChangeItem}
                    onDelete={handleDeleteItem}
                    onUnsavedChanges={handleUnsavedChanges}
                    shouldHighlight={
                      shouldHighlightUnsaved && unsavedChanges.has(faq.id)
                    }
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 mb-8">
          {isReadyStatus && mentorId ? (
            <>
              <Link
                data-testid="edit-faq-back-btn"
                to="/twin"
                className="btn btn-primary sm:!grow-0 !grow">
                <i className="fa-solid fa-arrow-left mr-2" />
                {t('twin.back')}
              </Link>
              <button
                data-testid="edit-faq-save-btn"
                className="btn btn-primary sm:!grow-0 !grow"
                onClick={handleSave}
                disabled={isUpdatingFaQs || !canSave}>
                {t('twin.update')}
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="edit-faq-back-btn"
                to="/twin/knowledge/files"
                className="btn btn-secondary sm:!grow-0 !grow">
                <i className="fa-solid fa-arrow-left mr-2" />
                {t('twin.back')}
              </Link>
              <button className="btn btn-primary" onClick={handleContinue}>
                {t('twin.continue')}
                <i className="fa-solid fa-arrow-right ml-2" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  },
);

export { FaqManagementSection };
